import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  fetchCategories, 
  fetchPhrases, 
  fetchPhraseById,
  searchPhrasesApi,
  getPhrasesByCategoryApi 
} from '@/api/phrasesApi';

const PhrasesContext = createContext(null);

export const PhrasesProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const retryCountRef = useRef(0);
  const isLoadingRef = useRef(false);
  const MAX_RETRIES = 3;

  // Load initial data - with retry mechanism and race condition prevention
  useEffect(() => {
    // Prevent re-fetching if data already loaded or already loading
    if (dataLoaded || isLoadingRef.current) return;
    
    const loadData = async () => {
      // Prevent concurrent loads
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Minimum loading time for animation (2 seconds)
        const minLoadTime = new Promise(resolve => setTimeout(resolve, 2000));
        
        const [categoriesData, phrasesData] = await Promise.all([
          fetchCategories(),
          fetchPhrases(),
          minLoadTime
        ]);
        
        // Validate data was received
        if (!categoriesData || categoriesData.length === 0) {
          throw new Error('No categories received from server');
        }
        if (!phrasesData || phrasesData.length === 0) {
          throw new Error('No phrases received from server');
        }
        
        setCategories(categoriesData);
        setPhrases(phrasesData);
        setDataLoaded(true);
        setIsLoading(false);
        retryCountRef.current = 0;
        isLoadingRef.current = false;
      } catch (err) {
        console.error('Failed to load data:', err);
        isLoadingRef.current = false;
        
        // Retry logic with exponential backoff (max 3 retries)
        if (retryCountRef.current < MAX_RETRIES) {
          const nextRetry = retryCountRef.current + 1;
          retryCountRef.current = nextRetry;
          const delay = 1000 * Math.pow(2, nextRetry - 1);
          setTimeout(() => loadData(), delay);
        } else {
          setError('Failed to load data. Please refresh the page.');
          setIsLoading(false);
        }
      }
    };

    loadData();
  }, [dataLoaded]);

  // Search phrases
  const searchPhrases = useCallback((query) => {
    if (!query || !query.trim()) {
      return phrases;
    }
    
    const lowerQuery = query.toLowerCase();
    return phrases.filter(p => 
      p.phrase.toLowerCase().includes(lowerQuery) ||
      p.actualMeaning?.toLowerCase().includes(lowerQuery) ||
      p.literalMeaning?.toLowerCase().includes(lowerQuery) ||
      p.context?.toLowerCase().includes(lowerQuery) ||
      (p.alternateSearches && p.alternateSearches.some(alt => alt.toLowerCase().includes(lowerQuery)))
    );
  }, [phrases]);

  // Get phrases by category
  const getPhrasesByCategory = useCallback((categoryId) => {
    if (!categoryId || categoryId === 'all') {
      return phrases;
    }
    return phrases.filter(p => p.category === categoryId);
  }, [phrases]);

  // Get phrase by ID
  const getPhraseById = useCallback((id) => {
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    return phrases.find(p => p.id === numId) || null;
  }, [phrases]);

  // Get category by ID
  const getCategoryById = useCallback((categoryId) => {
    return categories.find(c => c.id === categoryId) || null;
  }, [categories]);

  const value = {
    categories,
    phrases,
    isLoading,
    error,
    searchPhrases,
    getPhrasesByCategory,
    getPhraseById,
    getCategoryById
  };

  return (
    <PhrasesContext.Provider value={value}>
      {children}
    </PhrasesContext.Provider>
  );
};

export const usePhrases = () => {
  const context = useContext(PhrasesContext);
  if (!context) {
    throw new Error('usePhrases must be used within a PhrasesProvider');
  }
  return context;
};

export default PhrasesContext;
